import type { ReactNode } from 'react';

import { ChevronDownDashboardIcon as ChevronDownIcon } from '@truvhq/ui';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

import { COLLAPSE_TRANSITION } from 'features/onboarding/ui/ChecklistStepRow';

/**
 * What a control center tab puts above the chat: a strip that stays pinned under the tabs, and a list
 * the strip expands over the thread.
 *
 * The conversation renders the list, because only it knows where its thread ends and its composer
 * begins — the list sits behind the thread, and the thread slides off it. The conversation stays
 * mounted across tabs, so its scroll position and any in-flight stream survive switching.
 */
export type PaneSlot = {
  strip: ReactNode;
  list: ReactNode;
  isListOpen: boolean;
  /** Called when the user asks the assistant something, so the list gets out of the way. */
  onAsk: () => void;
};

export const EMPTY_PANE: PaneSlot = { strip: null, list: null, isListOpen: false, onAsk: () => undefined };

const useSlide = () => (useReducedMotion() ? { duration: 0 } : COLLAPSE_TRANSITION);

type PaneStripProps = {
  isListOpen: boolean;
  onToggle: () => void;
  children: ReactNode;
};

export function PaneStrip({ isListOpen, onToggle, children }: PaneStripProps) {
  const slide = useSlide();

  return (
    // Sits above the list so the list scrolls under it rather than across it.
    <div className="relative z-[6] flex-none border-grey30 border-b bg-white">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isListOpen}
        className="flex w-full cursor-pointer items-center gap-2.5 bg-transparent px-6 py-3 text-left max-md:px-4"
      >
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">{children}</span>
        <motion.span
          className="flex shrink-0"
          animate={{ rotate: isListOpen ? 180 : 0 }}
          transition={slide}
          aria-hidden
        >
          <ChevronDownIcon className="h-5 w-5 text-grey60" />
        </motion.span>
      </button>
    </div>
  );
}

export function PaneList({ isOpen, label, children }: { isOpen: boolean; label: string; children: ReactNode }) {
  const slide = useSlide();

  // Held through the conversation's slide back over it, so the list doesn't vanish from under a
  // surface that is still moving.
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.section
          aria-label={label}
          className="absolute inset-0 overflow-y-auto bg-grey20 px-6 py-4"
          initial={false}
          exit={{ opacity: 1 }}
          transition={slide}
        >
          {children}
        </motion.section>
      )}
    </AnimatePresence>
  );
}
